import { Badge, Button } from '@roger/ui-components';
import { Banknote, Pencil, RefreshCw } from 'lucide-react';

import {
  PAYOR_TYPE_LABELS,
  type AgencyInsuranceResponse,
} from '../../services/clinicsApi';
import { BILLING_FREQUENCY_OPTIONS } from '../../constants/insuranceBilling';
import { RETIRED_PAYER_LABEL } from '../../utils/insuranceOptions';

interface InsuranceHeaderProps {
  insurance: AgencyInsuranceResponse;
  clinicName: string | null;
  // The WellSky sync still owns this row (see isWellSkySyncedInsurance).
  wellSkySynced: boolean;
  onEdit?: (insurance: AgencyInsuranceResponse) => void;
  onEditRates?: (insurance: AgencyInsuranceResponse) => void;
}

// The edit form's own words for each frequency, so the two never differ.
const FREQUENCY_LABELS: Record<string, string> = Object.fromEntries(
  BILLING_FREQUENCY_OPTIONS.map(option => [option.value, option.label])
);

/** An insurance's settings, a few words each, for one quiet line. */
const insuranceSettings = (insurance: AgencyInsuranceResponse): string[] =>
  [
    insurance.noa_required ? 'NOA required' : 'No NOA',
    insurance.visit_authorization_required
      ? 'Prior auth required'
      : 'No prior auth',
    insurance.type_of_bill ? `Type of bill ${insurance.type_of_bill}` : null,
    insurance.billing_frequency
      ? (FREQUENCY_LABELS[insurance.billing_frequency] ??
        insurance.billing_frequency)
      : null,
    insurance.pdgm_enabled ? 'PDGM' : null,
  ].filter((part): part is string => Boolean(part));

/** Status chip: this insurance is owned by the WellSky sync. */
function WellSkySyncedBadge() {
  return (
    <Badge variant='secondary' className='gap-1 whitespace-nowrap font-normal'>
      <RefreshCw className='h-3 w-3' aria-hidden />
      WellSky
    </Badge>
  );
}

/** Status chip: WellSky has retired this payer. */
function RetiredBadge({ insurance }: { insurance: AgencyInsuranceResponse }) {
  return insurance.retired_in_wellsky ? (
    <Badge variant='outline' className='whitespace-nowrap font-normal'>
      {RETIRED_PAYER_LABEL}
    </Badge>
  ) : null;
}

function InsuranceStatusBadges({
  insurance,
  wellSkySynced,
}: {
  insurance: AgencyInsuranceResponse;
  wellSkySynced: boolean;
}) {
  if (!wellSkySynced && !insurance.retired_in_wellsky) {
    return null;
  }

  return (
    <div className='flex flex-wrap items-center gap-1.5'>
      {wellSkySynced && <WellSkySyncedBadge />}
      <RetiredBadge insurance={insurance} />
    </div>
  );
}

/**
 * The top of the insurance pane: who it is, its settings on one quiet line,
 * and the ways to change it. The settings are read here and edited in the
 * Edit dialog, so the pane stays short.
 *
 * Stacked (not one crowded flex row) so the name can wrap, status badges read
 * as chips, and Edit/Rates stay clear action buttons. Visit/patient detail
 * headers use the same title → metadata → badges → actions separation; nothing
 * shared was a clean fit for this Admin split pane, so the layout stays local.
 */
export function InsuranceHeader({
  insurance,
  clinicName,
  wellSkySynced,
  onEdit,
  onEditRates,
}: InsuranceHeaderProps) {
  const settings = insuranceSettings(insurance);

  return (
    <div className='flex flex-col gap-3'>
      <div className='min-w-0 space-y-2'>
        <h2 className='text-lg font-semibold break-words'>{insurance.name}</h2>
        <p className='text-sm text-muted-foreground'>
          {[
            clinicName,
            insurance.edi_payer_id
              ? `Payer ID ${insurance.edi_payer_id}`
              : 'No payer ID',
            insurance.payor_type
              ? PAYOR_TYPE_LABELS[insurance.payor_type]
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <InsuranceStatusBadges
          insurance={insurance}
          wellSkySynced={wellSkySynced}
        />
      </div>

      {(onEdit || onEditRates) && (
        <InsuranceActions
          insurance={insurance}
          onEdit={onEdit}
          onEditRates={onEditRates}
        />
      )}

      {settings.length > 0 && (
        <p className='text-xs text-muted-foreground'>{settings.join(' · ')}</p>
      )}
    </div>
  );
}

/**
 * One insurance under its payer in the pane: its clinic, its name and its
 * settings, with Edit, Rates and delete for that insurance alone.
 */
export function InsuranceLine({
  insurance,
  clinicName,
  wellSkySynced,
  highlighted,
  onEdit,
  onEditRates,
}: InsuranceHeaderProps & { highlighted: boolean }) {
  return (
    <li
      className={`flex flex-col gap-2 py-2 sm:flex-row sm:items-start sm:gap-2 sm:py-1.5 ${
        highlighted ? '-mx-2 rounded-md bg-muted px-2' : ''
      }`}
    >
      {/* The name and its clinic; the settings live in Edit. */}
      <div className='min-w-0 flex-1 space-y-1'>
        <div className='text-sm font-medium break-words'>{insurance.name}</div>
        <InsuranceStatusBadges
          insurance={insurance}
          wellSkySynced={wellSkySynced}
        />
        {clinicName && (
          <div className='text-xs text-muted-foreground break-words'>
            {clinicName}
          </div>
        )}
      </div>
      <InsuranceActions
        insurance={insurance}
        onEdit={onEdit}
        onEditRates={onEditRates}
      />
    </li>
  );
}

/**
 * Edit and Rates as buttons in plain sight, since they are what people come
 * for. Delete is inside Edit.
 */
export function InsuranceActions({
  insurance,
  onEdit,
  onEditRates,
}: Omit<InsuranceHeaderProps, 'clinicName' | 'wellSkySynced'>) {
  return (
    <div className='flex flex-wrap shrink-0 items-center gap-1'>
      {onEdit && (
        <Button
          size='sm'
          variant='outline'
          onClick={() => onEdit(insurance)}
          aria-label={`Edit ${insurance.name}`}
        >
          <Pencil className='mr-1.5 h-3.5 w-3.5' />
          Edit
        </Button>
      )}
      {onEditRates && (
        <Button
          size='sm'
          variant='outline'
          onClick={() => onEditRates(insurance)}
          aria-label={`Billing rates for ${insurance.name}`}
        >
          <Banknote className='mr-1.5 h-3.5 w-3.5' />
          Rates
        </Button>
      )}
    </div>
  );
}
